import groq from "groq"
import { v4 as uuidv4 } from "uuid"

export async function getOccasionReferences(client, selectedOccasions) {
  const selectedOccasionIds = selectedOccasions?.map((occasion) => occasion.id) ?? []

  const occasions = await client.fetch(groq`*[_type=="occasion" && _id in $selectedOccasionIds]`, {
    selectedOccasionIds,
  })

  return occasions.map((occasion) => {
    return { _type: "reference", _ref: occasion._id, _key: occasion._id, _weak: true }
  })
}

export function getUpdatedAttachments(visitReportAttachments, editedAttachments) {
  const updatedAttachments = editedAttachments?.map((editedAttachment) => {
    // if attachment id is already in sanity, attachment was not modified and we keep the attachment data stored in sanity
    const unmodifiedAttachment = visitReportAttachments?.find(
      (attachmentFromSanity) => attachmentFromSanity.id === editedAttachment.id,
    )
    return unmodifiedAttachment ? unmodifiedAttachment : editedAttachment
  })

  return updatedAttachments?.map((attachment) => {
    return {
      name: attachment.name,
      mimetype: attachment.mimetype,
      size: attachment.size,
      base64String: attachment.base64String,
      _key: uuidv4(),
    }
  })
}

export const hasAttachmentsChanged = (attachmentLengthInSanity, updatedAttachments) => {
  return (
    attachmentLengthInSanity !== updatedAttachments?.length || updatedAttachments?.some((attachment) => !attachment.id)
  )
}

export async function deleteAttachmentReferences(attachments, client) {
  if (attachments.length === 0) return null

  const results = []
  for (const attachment of attachments) {
    if (!attachment?.id || !attachment?.asset?._ref) continue

    try {
      const result = await client.delete(attachment.asset._ref)

      results.push(result)
    } catch (e) {
      console.error(e)
      // TODO handle this
    }
  }
  return results
}

export async function createAttachmentReferences(attachments, client) {
  if (attachments.length === 0) return null

  const results = []
  for (const attachment of attachments) {
    const type = fileOrImage(attachment?.mimetype)

    if (attachment?.id) {
      const { id, ...rest } = attachment
      results.push({ _key: id, _type: `${type}Reference`, ...rest })
    }

    if (!attachment?.base64String) continue

    const blob = b64toBlob(attachment?.base64String, attachments.mimetype)
    try {
      const result = await client.assets.upload(type, blob, {
        filename: attachment.name,
      })

      results.push({
        _type: `${type}Reference`,
        asset: {
          _ref: result._id,
          _type: "reference",
        },
        size: result.size,
        mimetype: result.mimeType,
        url: result.url,
        name: result.originalFilename,
        _key: uuidv4(),
      })
    } catch (e) {
      console.error({ e })
      // TODO handle this
    }
  }
  return results
}

function b64toBlob(b64Data, contentType = "", sliceSize = 512) {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentType })
}

function fileOrImage(mimeType) {
  const imageTypes = ["image/jpeg", "image/png"]

  if (imageTypes.includes(mimeType)) {
    return "image"
  } else {
    return "file"
  }
}
